import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CalendarIcon from 'assets/LeadInfo/calendar.svg';
import DateIcon from 'assets/LeadInfo/date.svg';
import HistoryIcon from 'assets/LeadInfo/history.svg';
import MessageIcon from 'assets/LeadInfo/message.svg';
import NoteIcon from 'assets/LeadInfo/note.svg';
import {
  completeFollowUp,
  getFollowUps,
  getLeadNotes,
} from 'features/newsales/newsalesActions';
import Tag from 'pages/components/tag';
import { openWhatsAppChat } from 'utils/chatUtils';

import FollowUpModal from '../followUpModal';
import LeadInfoModal from '../leadInfoModal';
import {
  ActionsItem,
  ActionsTitle,
  CardAuthor,
  CardDate,
  CardHeader,
  CardNote,
  DateImg,
  FollowUpCard,
  FollowUpsBox,
  FollowUpsContent,
  FollowUpsTitle,
  NoteCard,
  NoteDescription,
  NoteFooter,
  NoteTitle,
  QuickActionsBox,
  TitleImg,
} from './QuickActions.styles';
import AttachVivaRequestModal from './attachVivaRequestModal/AttachVivaRequestModal';
import OrderSelectModal from './orderSelectModal/OrderSelectModal';
import SetAppointmentModal from './setAppointmentModal/SetAppointmentModal';

const QuickActions = ({ leadData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [modalType, setModalType] = useState(null);
  const [notes, setNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(true);
  const [notesError, setNotesError] = useState(null);
  const [items, setItems] = useState([]);
  const followUps = items;

  const handleMarkAsDone = async (followUp) => {
    try {
      const res = await dispatch(
        completeFollowUp({
          leadId: leadData.id,
          followUpId: followUp.uuid || followUp.id,
        })
      );

      setItems((prev) =>
        prev.map((item) =>
          (item.uuid || item.id) === (followUp.uuid || followUp.id)
            ? {
                ...item,
                status: 'completed',
                completedAt: new Date(),
              }
            : item
        )
      );
    } catch (err) {
      console.error('COMPLETE FOLLOWUP ERROR:', err);
    }
  };
  const fetchNotes = useCallback(async () => {
    if (!leadData?.id) return;

    setIsLoadingNotes(true);
    setNotesError(null);
    try {
      const response = await dispatch(getLeadNotes(leadData.id)).unwrap();
      const notesArray = Array.isArray(response)
        ? response
        : response?.data
          ? Array.isArray(response.data)
            ? response.data
            : []
          : [];
      setNotes(notesArray);
    } catch (error) {
      setNotesError(error);
      setNotes([]);
    } finally {
      setIsLoadingNotes(false);
    }
  }, [leadData?.id, dispatch]);

  const normalizeStatus = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'done';
      case 'pending':
        return 'pending';
      default:
        return status;
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  useEffect(() => {
    if (leadData?.followUps) {
      setItems(leadData.followUps);
    }
  }, [leadData?.followUps]);

  const handleNoteSuccess = () => {
    fetchNotes();
  };
  const allOrders = (leadData?.offers || []).flatMap((offer) =>
    (offer.orders || [])
      .filter((order) => order.status?.toLowerCase() === 'approved')
      .map((order) => ({
        ...order,
        offerName: offer.name,
        offerId: offer.id,
      }))
  );
  const isReadyForInstallation =
    leadData?.workflowStatus?.outcomeType === 'ready_for_installation';

  const isAppointmentDisabled = allOrders.length === 0 || !isReadyForInstallation;

  const ERB_PRODUCT_IDS = [10, 13, 15, 16, 17, 21];
  const hasErbProduct = leadData?.products?.some((p) => ERB_PRODUCT_IDS.includes(p.erpId));
  const handleCreateAppointment = (orderId) => {
    const leadName = leadData?.companyName
      ? leadData.companyName
      : `${leadData?.firstName || ''} ${leadData?.lastName || ''}`.trim();
    const selectedOrder = allOrders.find((order) => order.id === orderId);
    navigate('/project-management/appointment/create-edit', {
      state: {
        lead: {
          id: leadData?.id,
          leadNumber: leadData?.leadNumber,

          name: leadName,
          orderId: orderId,
          phone: leadData?.phone,
          currentOffer: selectedOrder?.offerId || '',
        },
      },
    });
  };

  const isVivaLead = leadData?.type?.toLowerCase() === 'viva';

  const handleAfterVivaRequest = (_selected) => {
    setModalType(null);
    if (hasErbProduct) {
      setModalType('set-appointment');
    } else if (allOrders.length === 1) {
      handleCreateAppointment(allOrders[0].id);
    } else {
      setModalType('order-select');
    }
  };

  const handleSetAppointmentClick = () => {
    if (isVivaLead) {
      setModalType('attach-viva-request');
    } else if (hasErbProduct) {
      setModalType('set-appointment');
    } else if (allOrders.length === 1) {
      handleCreateAppointment(allOrders[0].id);
    } else {
      setModalType('order-select');
    }
  };

  const handleSetAppointmentSaved = () => {
    if (allOrders.length === 1) {
      handleCreateAppointment(allOrders[0].id);
    } else {
      setModalType('order-select');
    }
  };

  return (
    <>
      <QuickActionsBox>
        <ActionsTitle>Quick Actions</ActionsTitle>
        <ActionsItem onClick={() => setModalType('follow-up')}>
          <DateImg src={DateIcon} />
          Add Follow-up Date
        </ActionsItem>
        <ActionsItem onClick={openWhatsAppChat}>
          <DateImg src={MessageIcon} />
          Send WhatsApp
        </ActionsItem>
        <ActionsItem
          onClick={!isAppointmentDisabled ? handleSetAppointmentClick : undefined}
          disabled={isAppointmentDisabled}
        >
          <DateImg src={CalendarIcon} />
          Set Appointment
        </ActionsItem>
        <ActionsItem onClick={() => setModalType('note')}>
          <DateImg src={NoteIcon} />
          Add Note
        </ActionsItem>
      </QuickActionsBox>
      <FollowUpModal
        isOpen={modalType === 'follow-up'}
        leadId={leadData?.id}
        onClose={() => setModalType(null)}
        onSuccess={handleNoteSuccess}
      />
      <LeadInfoModal
        isOpen={modalType === 'note'}
        type={modalType}
        leadId={leadData?.id}
        onClose={() => setModalType(null)}
        onSuccess={handleNoteSuccess}
      />
      <OrderSelectModal
        isOpen={modalType === 'order-select'}
        onClose={() => setModalType(null)}
        orders={allOrders}
        onSelect={handleCreateAppointment}
      />
      <SetAppointmentModal
        isOpen={modalType === 'set-appointment'}
        onClose={() => setModalType(null)}
        leadData={leadData}
        onSaved={handleSetAppointmentSaved}
      />
      <AttachVivaRequestModal
        isOpen={modalType === 'attach-viva-request'}
        onClose={() => setModalType(null)}
        onConfirm={handleAfterVivaRequest}
        leadId={leadData.id}
      />
      {followUps.length > 0 && (
        <FollowUpsBox>
          <FollowUpsTitle>
            <TitleImg src={HistoryIcon} />
            Follow-ups
          </FollowUpsTitle>
          <FollowUpsContent>
            {items.map((item, index) => (
              <FollowUpCard
                key={item.uuid || item.id || index}
                status={normalizeStatus(item.status)}
              >
                <CardHeader>
                  {item.nextContactDate && (
                    <CardDate>
                      {`${new Date(item.nextContactDate).toLocaleDateString()} ${
                        item.nextContactTime || ''
                      }`}
                    </CardDate>
                  )}

                  {normalizeStatus(item.status) === 'done' ? (
                    <Tag type="status" variant="Done" />
                  ) : (
                    <button
                      onClick={() => handleMarkAsDone(item)}
                      style={{
                        marginLeft: 'auto',
                        padding: '6px 14px',
                        borderRadius: '16px',
                        border: '1px solid #2d6cdf',
                        background: '#eef4ff',
                        color: '#2d6cdf',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Mark as done
                    </button>
                  )}
                </CardHeader>

                {(item.createdBy?.name || item.createdBy?.email) && (
                  <CardAuthor>{item.createdBy.name || item.createdBy.email}</CardAuthor>
                )}

                {item.notes && <CardNote>{item.notes}</CardNote>}
              </FollowUpCard>
            ))}
          </FollowUpsContent>{' '}
        </FollowUpsBox>
      )}
      {/* Appointments Section - Commented out for now */}
      {/* <FollowUpsBox>
        <FollowUpsTitle>
          <TitleImg src={CalendarIcon} />
          Appointments
        </FollowUpsTitle>
        <FollowUpsContent>
          {APPOINTMENTS_DATA.map((app) => (
            <AppointmentCard key={app.id}>
              {(app.date || app.status) && (
                <AppointmentRow className="mobile-column">
                  {app.date && (
                    <InfoGroup>
                      <Label className="date-style">{app.date}</Label>
                    </InfoGroup>
                  )}
                  {app.status && (
                    <AppointmentBadge className="mobile-column-badge">
                      {app.status}
                    </AppointmentBadge>
                  )}
                </AppointmentRow>
              )}

              {app.ID && (
                <AppointmentRow isSmallMargin>
                  <InfoGroup>
                    <Label>ID:</Label>
                    <Value>{app.ID}</Value>
                  </InfoGroup>
                </AppointmentRow>
              )}

              {app.location && (
                <AppointmentRow isSmallMargin>
                  <InfoGroup>
                    <Label>Location:</Label>
                    <Value>{app.location}</Value>
                  </InfoGroup>
                </AppointmentRow>
              )}

              {app.description && (
                <InfoGroup vertical>
                  <Label>Description:</Label>
                  <DescriptionText>{app.description}</DescriptionText>
                </InfoGroup>
              )}

              {app.service && (
                <AppointmentRow isSmallMargin>
                  <InfoGroup>
                    <Label>Service:</Label>
                    <Value isSecondary>{app.service}</Value>
                  </InfoGroup>
                </AppointmentRow>
              )}

              {app.subService && (
                <AppointmentRow isSmallMargin>
                  <InfoGroup>
                    <Label>Sub-service:</Label>
                    <Value isSecondary>{app.subService}</Value>
                  </InfoGroup>
                </AppointmentRow>
              )}

              {app.offer && (
                <AppointmentRow isSmallMargin>
                  <InfoGroup>
                    <Label>Offer:</Label>
                    <Value isSecondary>{app.offer}</Value>
                  </InfoGroup>
                </AppointmentRow>
              )}

              {app.frequency && (
                <AppointmentRow isSmallMargin>
                  <InfoGroup>
                    <Label>Frequency:</Label>
                    <Value isSecondary>{app.frequency}</Value>
                  </InfoGroup>
                </AppointmentRow>
              )}

              {app.phone && (
                <AppointmentRow isSmallMargin>
                  <InfoGroup>
                    <Label>Phone call:</Label>
                    <Value>{app.phone}</Value>
                  </InfoGroup>
                </AppointmentRow>
              )}

              {app.assignee && (
                <AppointmentRow isSmallMargin>
                  <InfoGroup>
                    <Label>Assignee:</Label>
                    <Value isSecondary>{app.assignee}</Value>
                  </InfoGroup>
                </AppointmentRow>
              )}

              {app.created && (
                <AppointmentRow isSmallMargin>
                  <InfoGroup>
                    <Label>Created by:</Label>
                    <Value isSecondary>{app.created}</Value>
                  </InfoGroup>
                </AppointmentRow>
              )}
            </AppointmentCard>
          ))}
        </FollowUpsContent>
      </FollowUpsBox> */}
      {!isLoadingNotes && !notesError && notes.length > 0 && (
        <FollowUpsBox>
          <FollowUpsTitle>
            <TitleImg src={NoteIcon} />
            Notes
          </FollowUpsTitle>
          <FollowUpsContent>
            {notes.map((note) => (
              <NoteCard key={note.id}>
                {note.title && <NoteTitle>{note.title}</NoteTitle>}
                {note.description && <NoteDescription>{note.description}</NoteDescription>}
                {(note.createdAt || note.createdBy?.fullName || note.author) && (
                  <NoteFooter>
                    {note.createdAt &&
                      new Date(note.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    {note.createdAt && (note.createdBy?.fullName || note.author) && ' • '}
                    {note.createdBy?.fullName || note.author || ''}
                  </NoteFooter>
                )}
              </NoteCard>
            ))}
          </FollowUpsContent>
        </FollowUpsBox>
      )}
    </>
  );
};

export default QuickActions;
